import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:pointer-events-none disabled:opacity-50 disabled:bg-bg-secondary disabled:text-accent-muted disabled:shadow-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary action - use rare rarity color
        default:
          "bg-rarity-rare text-bg-primary shadow-md hover:bg-rarity-rare/90 active:scale-95",
        // Destructive - use danger color
        destructive:
          "bg-danger text-bg-primary shadow-md hover:bg-danger/90 active:scale-95",
        // Secondary - use secondary background
        secondary:
          "bg-bg-secondary text-text-primary border border-border-primary shadow-sm hover:bg-bg-active",
        // Outline - use border color
        outline:
          "border border-border-primary bg-transparent text-text-primary hover:bg-bg-hover active:bg-bg-active",
        // Ghost - minimal, hover only
        ghost:
          "text-text-primary hover:bg-bg-hover hover:text-text-primary",
        // Link - styled as link
        link:
          "text-rarity-rare underline-offset-4 hover:underline",
        // Success variant for game actions
        success:
          "bg-success text-bg-primary shadow-md hover:bg-success/90 active:scale-95",
        // Warning variant
        warning:
          "bg-warning text-bg-primary shadow-md hover:bg-warning/90 active:scale-95",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
